/**
 * Marks non-swatch option values that have no matching variant for the other
 * currently selected options (Production cascade).
 *
 * Example: Matte + 2x4 is unavailable → selecting 2x4 marks Matte sold-out,
 * and selecting Matte marks 2x4 sold-out.
 *
 * Sold-out / missing non-swatch options look disabled but stay fully clickable
 * so shoppers can explore other combinations. Swatches are never visually
 * changed. Current selections are never auto-corrected.
 */
(function () {
  const DISABLED_CLASS = 'variant-option--unavailable';

  function isPrimaryPicker(picker) {
    return !picker.parentElement?.closest('variant-selects, variant-radios');
  }

  function isElementHidden(element) {
    if (element.closest('.material-hidden-slc, [hidden], .hidden')) return true;

    const style = window.getComputedStyle(element);
    return style.display === 'none' || style.visibility === 'hidden';
  }

  function getOptionGroups(picker) {
    const groups = [];

    if (picker.tagName === 'VARIANT-SELECTS') {
      Array.from(picker.children).forEach((child) => {
        if (child.tagName === 'VARIANT-RADIOS') {
          child.querySelectorAll(':scope > fieldset').forEach((fieldset) => {
            groups.push({ type: 'radio', element: fieldset });
          });
          return;
        }

        if (child.classList.contains('material-hidden-slc')) return;

        const select = child.querySelector('select');
        if (select) {
          groups.push({ type: 'select', element: select });
        }
      });

      return groups;
    }

    if (picker.tagName === 'VARIANT-RADIOS') {
      picker.querySelectorAll(':scope > fieldset').forEach((fieldset) => {
        groups.push({ type: 'radio', element: fieldset });
      });
    }

    return groups;
  }

  function isGroupVisible(group) {
    return !isElementHidden(group.element);
  }

  function isSwatchControl(input) {
    if (!input) return false;
    if (input.closest('.swatch, .variant-swatch')) return true;

    let label = input.nextElementSibling;
    if (label?.tagName !== 'LABEL' && input.id) {
      const escapedId =
        typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
          ? CSS.escape(input.id)
          : input.id.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
      label = input.parentElement?.querySelector(`label[for="${escapedId}"]`) || null;
    }

    return Boolean(
      label?.classList?.contains('swatch__label') || label?.classList?.contains('variant-swatch__label')
    );
  }

  function isSwatchGroup(group) {
    if (group.type !== 'radio') return false;

    const fieldset = group.element;
    if (
      fieldset.classList.contains('product-form__input--swatches') ||
      fieldset.classList.contains('product-form__input--frame-swatches')
    ) {
      return true;
    }

    return Boolean(
      fieldset.querySelector('.swatch, .variant-swatch, label.swatch__label, label.variant-swatch__label')
    );
  }

  function getVariantData(picker) {
    const script =
      picker.querySelector(':scope > script[type="application/json"]') ||
      picker.querySelector('script[type="application/json"]');

    if (!script) return [];

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      return [];
    }
  }

  function getVariantOptions(variant) {
    if (Array.isArray(variant.options) && variant.options.length) return variant.options;
    return [variant.option1, variant.option2, variant.option3].filter(Boolean);
  }

  function getSelectedOptions(groups) {
    return groups.map((group) => {
      if (group.type === 'radio') {
        const checked = group.element.querySelector('input[type="radio"]:checked');
        return checked ? checked.value : '';
      }

      return group.element.value || '';
    });
  }

  /**
   * Production rule: value is valid only if some variant matches it together
   * with every other currently selected option (bidirectional).
   */
  function variantExists(variants, optionIndex, optionValue, selectedOptions) {
    return variants.some((variant) => {
      const options = getVariantOptions(variant);

      return options.every((value, index) => {
        if (index === optionIndex) return value === optionValue;

        const selected = selectedOptions[index];
        if (!selected) return true;

        return value === selected;
      });
    });
  }

  function variantAvailable(variants, optionIndex, optionValue, selectedOptions) {
    const matches = variants.filter((variant) => {
      const options = getVariantOptions(variant);

      return options.every((value, index) => {
        if (index === optionIndex) return value === optionValue;

        const selected = selectedOptions[index];
        if (!selected) return true;

        return value === selected;
      });
    });

    if (!matches.length) return false;
    return matches.some((variant) => variant.available);
  }

  function resolveLabel(input) {
    let label = input.nextElementSibling;
    if (label?.tagName !== 'LABEL' && input.id) {
      const escapedId =
        typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
          ? CSS.escape(input.id)
          : input.id.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
      label = input.parentElement?.querySelector(`label[for="${escapedId}"]`) || null;
    }
    return label?.tagName === 'LABEL' ? label : null;
  }

  function clearSwatchControl(input) {
    input.disabled = false;
    input.classList.remove(DISABLED_CLASS);
    resolveLabel(input)?.classList.remove(DISABLED_CLASS);
    input.closest('.swatch, .variant-swatch')?.classList.remove(DISABLED_CLASS);
  }

  function setRadioAppearance(input, isAvailable) {
    if (isSwatchControl(input)) {
      clearSwatchControl(input);
      return;
    }

    // Visual only — keep clickable so shoppers can explore combinations.
    input.disabled = false;
    input.classList.toggle(DISABLED_CLASS, !isAvailable);
    resolveLabel(input)?.classList.toggle(DISABLED_CLASS, !isAvailable);
  }

  function setSelectOptionAppearance(option, isAvailable) {
    option.disabled = false;
    option.classList.toggle(DISABLED_CLASS, !isAvailable);
  }

  function clearGroupAvailability(group) {
    if (group.type === 'radio') {
      group.element.querySelectorAll('input[type="radio"]').forEach((input) => {
        setRadioAppearance(input, true);
      });
      return;
    }

    Array.from(group.element.options).forEach((option) => {
      if (!option.value) return;
      setSelectOptionAppearance(option, true);
    });
  }

  function updateGroupAvailability(group, optionIndex, variants, selectedOptions) {
    if (isSwatchGroup(group)) {
      clearGroupAvailability(group);
      return;
    }

    if (group.type === 'radio') {
      group.element.querySelectorAll('input[type="radio"]').forEach((input) => {
        const exists = variantExists(variants, optionIndex, input.value, selectedOptions);
        const available =
          exists && variantAvailable(variants, optionIndex, input.value, selectedOptions);
        setRadioAppearance(input, available);
      });
      return;
    }

    Array.from(group.element.options).forEach((option) => {
      if (!option.value) return;
      const exists = variantExists(variants, optionIndex, option.value, selectedOptions);
      const available =
        exists && variantAvailable(variants, optionIndex, option.value, selectedOptions);
      setSelectOptionAppearance(option, available);
    });
  }

  function updatePicker(picker) {
    if (!picker || !isPrimaryPicker(picker)) return;

    const variants = getVariantData(picker);
    const groups = getOptionGroups(picker);
    if (!variants.length || !groups.length) return;

    const selectedOptions = getSelectedOptions(groups);

    groups.forEach((group, optionIndex) => {
      if (!isGroupVisible(group)) return;
      updateGroupAvailability(group, optionIndex, variants, selectedOptions);
    });
  }

  function refreshAllPickers() {
    document.querySelectorAll('variant-radios, variant-selects').forEach((picker) => {
      updatePicker(picker);
    });
  }

  function handleChange(event) {
    const target = event.target;
    if (
      target.tagName !== 'SELECT' &&
      !(target.matches?.('input[type="radio"]') && target.closest('variant-radios, variant-selects'))
    ) {
      return;
    }

    const picker = target.closest('variant-radios, variant-selects');
    if (!picker) return;

    const primaryPicker = isPrimaryPicker(picker)
      ? picker
      : picker.closest('variant-selects, variant-radios');

    if (!primaryPicker || !isPrimaryPicker(primaryPicker)) return;

    updatePicker(primaryPicker);
  }

  function init() {
    if (window.VariantPicker?._initialized) {
      refreshAllPickers();
      return;
    }

    document.addEventListener('change', handleChange, true);
    refreshAllPickers();

    window.VariantPicker = {
      _initialized: true,
      update: updatePicker,
      refreshAll: refreshAllPickers,
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

